import { SocialLogin } from "@capgo/capacitor-social-login"
import type { GoogleLoginOptions } from "@capgo/capacitor-social-login"

const webClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined

// Google sign-in is a native Credential Manager flow, so it exists only in the
// Capacitor build. Google also refuses OAuth inside an embedded WebView, so
// there is no web fallback worth offering here — the other builds keep the
// username/password and guest paths they already have.
export const isGoogleLoginAvailable =
  import.meta.env.VITE_BUILD_TARGET === "mobile" && !!webClientId

// Signing out has to stick. Without this, the silent attempt on the login
// screen would sign the user straight back in and there would be no way off
// the account short of clearing app data.
const AUTO_SIGN_IN_SUPPRESSED_KEY = "google-auto-signin-suppressed"

export const suppressGoogleAutoSignIn = () => {
  try {
    localStorage.setItem(AUTO_SIGN_IN_SUPPRESSED_KEY, "1")
  } catch {
    // Storage can be unavailable; losing the flag only costs an extra tap.
  }
}

const allowGoogleAutoSignIn = () => {
  try {
    localStorage.removeItem(AUTO_SIGN_IN_SUPPRESSED_KEY)
  } catch {
    /* see above */
  }
}

const isAutoSignInSuppressed = () => {
  try {
    return localStorage.getItem(AUTO_SIGN_IN_SUPPRESSED_KEY) === "1"
  } catch {
    return false
  }
}

let initPromise: Promise<void> | null = null

const ensureInitialized = () => {
  if (!initPromise) {
    initPromise = SocialLogin.initialize({ google: { webClientId } }).catch((err) => {
      // Cleared so a later attempt retries instead of being stuck behind one
      // bad startup — Play Services can simply be updating when we first ask.
      initPromise = null
      throw err
    })
  }
  return initPromise
}

// Backing out of the sheet rejects the same way a real failure does, and there
// is nothing to report when someone just changed their mind.
const isUserCancellation = (err: unknown) => {
  const message = err instanceof Error ? err.message : String(err ?? "")
  return /cancel|dismiss/i.test(message)
}

const getIdToken = async (options: GoogleLoginOptions) => {
  const { result } = await SocialLogin.login({ provider: "google", options })
  // Online mode (the default) is what carries the ID token; the offline branch
  // returns a serverAuthCode instead and would need a different backend path.
  if (result.responseType !== "online" || !result.idToken) {
    throw new Error("Google sign-in returned no ID token")
  }
  return result.idToken
}

/**
 * Zero-tap sign-in for someone who has used Google with this app before: the
 * bottom sheet flashes past and resolves on its own. This is the Credential
 * Manager replacement for the old silentSignIn, and it is expected to fail
 * often — first install, several accounts on the device, none authorized yet —
 * so it never throws and never reports anything to the user.
 */
export const attemptGoogleAutoSignIn = async (): Promise<string | null> => {
  if (!isGoogleLoginAvailable || isAutoSignInSuppressed()) return null

  try {
    await ensureInitialized()
    return await getIdToken({
      style: "bottom",
      filterByAuthorizedAccounts: true,
      autoSelectEnabled: true,
    })
  } catch {
    // Nothing to say: the user never asked for this attempt in the first place.
    return null
  }
}

/**
 * The tap-the-button path. Tries the bottom sheet across every Google account
 * on the device first, since picking from a list beats the full screen flow,
 * and falls back to the standard button — which is also the only branch that
 * can add an account to a device that has none.
 *
 * Resolves to null when the user backs out; throws only on a real failure.
 */
export const signInWithGoogle = async (): Promise<string | null> => {
  await ensureInitialized()

  try {
    return await getIdToken({ style: "bottom", filterByAuthorizedAccounts: false })
  } catch (err) {
    if (isUserCancellation(err)) return null

    try {
      return await getIdToken({ style: "standard" })
    } catch (fallbackErr) {
      if (isUserCancellation(fallbackErr)) return null
      throw fallbackErr
    }
  }
}

/** Called once a sign-in of any kind succeeds, so auto sign-in is armed again. */
export const onGoogleSignInSucceeded = allowGoogleAutoSignIn

/**
 * Drops the Credential Manager's own record of the session. Best-effort: the
 * app's session is the JWT in localStorage, and that is cleared regardless.
 */
export const googleSignOut = async () => {
  if (!isGoogleLoginAvailable) return
  try {
    await SocialLogin.logout({ provider: "google" })
  } catch {
    // Never signed in with Google, or already signed out.
  }
}
